import { useEffect, useState } from 'react';
import { adminApi } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import type { SystemPromptTemplateDetail, CreateSystemPromptTemplateRequest, UpdateSystemPromptTemplateRequest } from '../../types/api';

type ModalMode = 'create' | 'edit' | null;

export default function PromptsPage() {
  const [templates, setTemplates] = useState<SystemPromptTemplateDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Modal state
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editingTemplate, setEditingTemplate] = useState<SystemPromptTemplateDetail | null>(null);
  const [formData, setFormData] = useState<CreateSystemPromptTemplateRequest>({
    name: '',
    category: '',
    content: '',
    description: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const data = await adminApi.getAllPromptTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Failed to load templates:', error);
      setErrorMessage('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 5000);
  };

  const openCreateModal = () => {
    setFormData({ name: '', category: '', content: '', description: '' });
    setEditingTemplate(null);
    setModalMode('create');
  };

  const openEditModal = (template: SystemPromptTemplateDetail) => {
    setFormData({
      name: template.name,
      category: template.category || '',
      content: template.content,
      description: template.description || '',
    });
    setEditingTemplate(template);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingTemplate(null);
    setFormData({ name: '', category: '', content: '', description: '' });
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      showError('Name and content are required');
      return;
    }

    setIsSaving(true);
    try {
      if (modalMode === 'create') {
        await adminApi.createPromptTemplate(formData);
        showSuccess('Template created successfully!');
      } else if (modalMode === 'edit' && editingTemplate) {
        const updateData: UpdateSystemPromptTemplateRequest = {
          name: formData.name,
          category: formData.category || undefined,
          content: formData.content,
          description: formData.description || undefined,
        };
        await adminApi.updatePromptTemplate(editingTemplate.id, updateData);
        showSuccess('Template updated successfully!');
      }
      closeModal();
      await loadTemplates();
    } catch (error: any) {
      showError(error.message || 'Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetDefault = async (templateId: string) => {
    try {
      await adminApi.setDefaultPromptTemplate(templateId);
      showSuccess('Default template updated!');
      await loadTemplates();
    } catch (error: any) {
      showError(error.message || 'Failed to set default');
    }
  };

  const handleDelete = async (template: SystemPromptTemplateDetail) => {
    if (template.isDefault) {
      showError('Cannot delete the default template');
      return;
    }
    if (template.assignedUserCount > 0) {
      showError(`Cannot delete: ${template.assignedUserCount} user(s) assigned to this template`);
      return;
    }
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }
    
    try {
      await adminApi.deletePromptTemplate(template.id);
      showSuccess('Template deleted successfully!');
      await loadTemplates();
    } catch (error: any) {
      showError(error.message || 'Failed to delete template');
    }
  };

  const handleToggleActive = async (template: SystemPromptTemplateDetail) => {
    try {
      await adminApi.updatePromptTemplate(template.id, { isActive: !template.isActive });
      showSuccess(`Template ${template.isActive ? 'deactivated' : 'activated'}!`);
      await loadTemplates();
    } catch (error: any) {
      showError(error.message || 'Failed to update template');
    }
  };

  const getCategoryColor = (category?: string) => {
    if (!category) return 'bg-gray-100 text-gray-700';
    const colors: Record<string, string> = {
      'General': 'bg-blue-100 text-blue-700',
      'Accessibility': 'bg-green-100 text-green-700',
      'Technical': 'bg-purple-100 text-purple-700',
      'Senior-Friendly': 'bg-amber-100 text-amber-700',
      'Kid-Safe': 'bg-pink-100 text-pink-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Prompt Templates</h1>
          <p className="text-gray-600 mt-1">Manage prompt templates for different user types</p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-wise text-white rounded-lg hover:bg-wise-dark flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Template
        </button>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {errorMessage}
        </div>
      )}

      {/* Template List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Template
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Users
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {templates.map((template) => (
              <tr key={template.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{template.name}</span>
                    {template.isDefault && (
                      <span className="text-yellow-500" title="Default template">⭐</span>
                    )}
                  </div>
                  {template.description && (
                    <p className="text-sm text-gray-500 mt-1 truncate max-w-md">{template.description}</p>
                  )}
                </td>
                <td className="px-6 py-4">
                  {template.category && (
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(template.category)}`}>
                      {template.category}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={() => handleToggleActive(template)}
                    disabled={template.isDefault && template.isActive}
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      template.isActive
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={template.isDefault ? 'Cannot deactivate default template' : `Click to ${template.isActive ? 'deactivate' : 'activate'}`}
                  >
                    {template.isActive ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {template.assignedUserCount > 0 ? (
                    <span className="text-wise font-medium">{template.assignedUserCount} user{template.assignedUserCount > 1 ? 's' : ''}</span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => openEditModal(template)}
                      className="text-wise hover:text-wise-dark px-2 py-1 text-sm"
                    >
                      Edit
                    </button>
                    {!template.isDefault && (
                      <button
                        onClick={() => handleSetDefault(template.id)}
                        disabled={!template.isActive}
                        className="text-amber-600 hover:text-amber-700 px-2 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        title={template.isActive ? 'Set as default' : 'Activate template first'}
                      >
                        Set Default
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(template)}
                      disabled={template.isDefault || template.assignedUserCount > 0}
                      className="text-red-600 hover:text-red-700 px-2 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      title={template.isDefault ? 'Cannot delete default' : template.assignedUserCount > 0 ? 'Reassign users first' : 'Delete template'}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {templates.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No templates found. Create your first template.</p>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Available variables:</strong> {'{{'}USER_NAME{'}}'}, {'{{'}TECH_SKILL_LEVEL{'}}'}, {'{{'}PREFERENCES{'}}'}, 
          {'{{'}USER_NOTES{'}}'}, {'{{'}USER_RULES{'}}'}, {'{{'}RELEVANT_DEVICES{'}}'}, {'{{'}INTENT_INSTRUCTIONS{'}}'}
        </p>
      </div>

      {/* Create/Edit Modal */}
      {modalMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                {modalMode === 'create' ? 'Create New Template' : `Edit Template: ${editingTemplate?.name}`}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Senior-Friendly"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wise focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category (Type)</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g., Accessibility, Technical, General"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wise focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of when to use this template"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-wise focus:border-transparent"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">Content *</label>
                  <span className="text-xs text-gray-500">{formData.content.length} characters</span>
                </div>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={15}
                  placeholder="Enter the system prompt template content..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-wise focus:border-transparent"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !formData.name.trim() || !formData.content.trim()}
                className="px-4 py-2 bg-wise text-white rounded-lg hover:bg-wise-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : modalMode === 'create' ? 'Create Template' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
